import { DatePipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    Injector,
    inject,
    input,
    model,
    type OnInit,
    output,
    resource,
    signal,
    type WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, type Data, type Params, Router, RouterModule } from '@angular/router';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { DataView as pDataView } from '@openng/optimus-ui/dataview';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { IconFieldModule } from '@openng/optimus-ui/iconfield';
import { InputIconModule } from '@openng/optimus-ui/inputicon';
import { InputNumberModule } from '@openng/optimus-ui/inputnumber';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { ListboxModule } from '@openng/optimus-ui/listbox';
import { MessageModule } from '@openng/optimus-ui/message';
import { PanelModule } from '@openng/optimus-ui/panel';
import { PopoverModule } from '@openng/optimus-ui/popover';
import { RadioButtonModule } from '@openng/optimus-ui/radiobutton';
import { type SelectChangeEvent, SelectModule } from '@openng/optimus-ui/select';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { SplitterModule } from '@openng/optimus-ui/splitter';
import { TagModule } from '@openng/optimus-ui/tag';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { cloneDeep, isNil, sortBy, toNumber } from 'lodash-es';
import { injectParams } from 'ngxtension/inject-params';
import { linkedQueryParam, paramToNumber } from 'ngxtension/linked-query-param';
import {
    auditTime,
    combineLatest,
    defer,
    distinctUntilChanged,
    forkJoin,
    iif,
    lastValueFrom,
    map,
    type Observable,
    of,
    startWith,
    switchMap,
} from 'rxjs';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import {
    RISK_DOMAINS,
    RISK_TYPES,
    TASK_TYPES,
} from '@app/modules/assets/components/reports/reports-details/reports-details.component';
import { PIPELINE_TYPE } from '@app/modules/assets/models/pipeline-builder.model';
import type { PromptAdapter } from '@app/modules/assets/models/prompt.model';
import type { ReportAdapter, ReportDataSource } from '@app/modules/assets/models/reports.model';
import { PromptService } from '@app/modules/assets/services/prompt.service';
import { ReportsService } from '@app/modules/assets/services/reports.service';
import { SimulationService } from '@app/modules/testing/services/simulation.service';
import {
    CalendarHelper,
    JOB_RECURRENCE_CYCLE,
    JOB_RECURRENCE_DAY,
    RECURRENCE_CYCLE,
} from '@app/shared/calendar-helper';
import { AddMoreComponent } from '@app/shared/components/add-more/add-more.component';
import { CodeMirrorComponent } from '@app/shared/components/code-mirror/code-mirror.component';
import { DefinitionComponent } from '@app/shared/components/definition/definition.component';
import { DescriptionComponent } from '@app/shared/components/description/description.component';
import { DialogBoxComponent } from '@app/shared/components/dialog-box/dialog-box.component';
import { DiffViewerComponent } from '@app/shared/components/diff-viewer/diff-viewer.component';
import {
    DynamicFieldComponent,
    type DynamicFormField,
} from '@app/shared/components/dynamic-form/dynamic-field.component';
import { FieldErrorComponent } from '@app/shared/components/field-error/field-error.component';
import { InputContainerComponent } from '@app/shared/components/input-container/input-container.component';
import { InputEditorComponent } from '@app/shared/components/input-editor/input-editor.component';
import { InputSelectComponent } from '@app/shared/components/input-select/input-select.component';
import {
    JsonEditorComponent,
    type SchemaWithExamples,
} from '@app/shared/components/json-editor/json-editor.component';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { PromptDefinitionComponent } from '@app/shared/components/prompt-definition/prompt-definition.component';
import { JobRuntimeInfoComponent } from '@app/shared/components/run-job/run-job-details/job-runtime-info/job-runtime-info.component';
import { VIEW_TYPE } from '@app/shared/components/run-job/run-job-details/run-job-details.constants';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import { SkeletonLoaderComponent } from '@app/shared/components/skeleton/skeleton-loader.component';
import { CustomHotTableComponent } from '@app/shared/components/spreadsheet/custom-hot-table.component';
import { StatusIconComponent } from '@app/shared/components/status-icon/status-icon.component';
import {
    EVALUATION_METHODS,
    EXECUTION_TYPE,
    GGX_GLOBAL_OBJECTS,
    INPUT_CASES,
    JOB_SAMPLE_TYPES,
    LINEAGE_CASES,
    MODEL_INPUT_TYPE,
    MODULES,
    OBJECT_DISPLAY_NAMES,
    OBJECTS,
    REPORT_PARAMETER_TYPE,
    SCHEDULED_JOB_STATUS,
    SKELETON_TYPES,
    WORKFLOW_STATUS,
    WORKFLOWABLE_OBJECTS,
} from '@app/shared/constants/shared.constant';
import type { NgActivatedRoute } from '@app/shared/interface/ng-router.interface';
import { CodeSuggestionItem } from '@app/shared/models/code-suggestion-item.model';
import type { InputResponseModel } from '@app/shared/models/input-response.model';
import {
    EVALUATION_TYPE,
    type JobAdapter,
    type JobFormGroup,
    JobModel,
    type MetricThreshold,
    type ReportParameterAdapter,
    type SimulationFigure,
    type SimulationMetric,
} from '@app/shared/models/job.model';
import { LocalDatePipe } from '@app/shared/pipes/date-format.pipe';
import { FindItemPipe } from '@app/shared/pipes/find-item.pipe';
import { IncludesPipe } from '@app/shared/pipes/includes.pipe';
import { JoinPipe } from '@app/shared/pipes/join.pipe';
import { TimeTakenPipe } from '@app/shared/pipes/time-taken.pipe';
import { TruncatePipe } from '@app/shared/pipes/truncate.pipe';
import { AttachmentService } from '@app/shared/services/attachment.service';
import { InferenceService } from '@app/shared/services/inference-service';
import { InputService } from '@app/shared/services/input.service';
import type { ItemResponse, WithCrudService } from '@app/shared/services/mixins/crud.service';
import type { WithExportableService } from '@app/shared/services/mixins/export.service';
import { WithJobService } from '@app/shared/services/mixins/job.service';
import type { WithLineageService } from '@app/shared/services/mixins/lineage.service';
import { PermissionService } from '@app/shared/services/permission.service';
import { PlatformSettingService } from '@app/shared/services/platform-setting.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { WindowService } from '@app/shared/services/window.service';
import { FormHelper } from '@app/shared/utils/form-helper';
import { Helpers } from '@app/shared/utils/helpers';
import { OBJECT_SERVICE_MAPPER } from '@app/shared/utils/object-service-mapper';
import { TAB_ROUTES } from '@app/shared/utils/route-configs';
import { RouterHelper } from '@app/shared/utils/router-helper';

export interface RunJobQueryParams {
    type: EXECUTION_TYPE;
    entityId: number;
    jobId?: number; // In case of rerun
    rerunReports?: boolean; // In case of rerun reports
    submissionDate?: string; // Used for calendar view monitoring
    baseHillclimbSimId?: number; // Used for hill climbing only
}

enum EVENT_TYPE {
    DEFAULT = 'default',
    REPLACE = 'replace',
    NEW = 'new',
}

const REGISTERED_TABLES_DATASOURCE: ReportDataSource = {
    id: -1, // Our own hack for a ID
    name: 'Use Registered Tables',
    icon: 'pi-database',
    description: 'Use existing registered tables for the data source',
    featureVersionIds: [],
    tableIds: [],
    definition: null,
    foundationModelVersionIds: [],
    promptVersionIds: [],
    ragVersionIds: [],
    inputType: MODEL_INPUT_TYPE.PYTHON_FUNCTION,
};

const CUSTOM_DATA_DATASOURCE: ReportDataSource = {
    id: -2, // Our own hack for a ID
    name: 'Upload Custom Data',
    icon: 'pi-upload',
    description: 'Provide/Upload your own data for this evaluation',
    featureVersionIds: [],
    tableIds: [],
    definition: null,
    foundationModelVersionIds: [],
    promptVersionIds: [],
    ragVersionIds: [],
    inputType: MODEL_INPUT_TYPE.DATASOURCE,
};

const AI_GENERATED_DATASOURCE: ReportDataSource = {
    id: -3, // Our own hack for a ID
    name: 'Generate Data With AI',
    icon: 'pi-sparkles',
    description: 'Generate synthetic data for this evaluation using AI',
    featureVersionIds: [],
    tableIds: [],
    definition: null,
    foundationModelVersionIds: [],
    promptVersionIds: [],
    ragVersionIds: [],
    inputType: MODEL_INPUT_TYPE.DATASOURCE,
};

const DEFAULT_DATASOURCES: ReportDataSource[] = [
    REGISTERED_TABLES_DATASOURCE,
    CUSTOM_DATA_DATASOURCE,
    AI_GENERATED_DATASOURCE,
];

const ANNOTATION_QUEUE_DATASOURCE: ReportDataSource = {
    id: -4, // Our own hack for a ID
    name: 'Use Annotation Queue Data',
    icon: 'pi-inbox',
    description: 'Use annotation queue data for this evaluation',
    featureVersionIds: [],
    tableIds: [],
    definition: null,
    foundationModelVersionIds: [],
    promptVersionIds: [],
    ragVersionIds: [],
    inputType: MODEL_INPUT_TYPE.PYTHON_FUNCTION,
};

const BEDROCK_AUTH_SCHEMA = {
    properties: {
        awsAuthType: { type: 'string', enum: ['env', 'role', 'ec2'] },
        roleToAssume: { type: 'string' },
        roleSessionName: { type: 'string' },
        awsRegion: { type: 'string' },
        awsAccessKeyId: { type: 'string' },
        awsSecretAccessKey: { type: 'string' },
        awsSessionToken: { type: 'string' },
    },
} as const;

const TESTER_LLM_CONFIG_SCHEMA: SchemaWithExamples = {
    schema: {
        type: 'object',
        additionalProperties: false,
        required: ['modelProvider', 'modelName'],
        properties: {
            maxTurns: { type: 'integer', minimum: 1 },
            stoppingFields: { type: 'array', items: { type: 'string' } },
            modelProvider: {
                type: 'string',
                enum: ['openai', 'anthropic', 'azure', 'aws-bedrock', 'deepseek', 'google'],
            },
            modelName: { type: 'string' },
            modelConfigs: { type: 'object', additionalProperties: true },
            apiKey: { type: 'string' },
            apiEndpoint: { type: 'string' },
            apiVersion: { type: 'string' },
            ...BEDROCK_AUTH_SCHEMA.properties,
        },
    },
    examples: [
        {
            title: 'Tester LLM Configuration Schema',
            description: 'Includes auto-chat model settings and stopping conditions',
            value: {
                maxTurns: 4,
                stoppingFields: ['severity', 'summary'],
                modelProvider: 'aws-bedrock',
                modelName: 'anthropic.claude-3-5-sonnet',
                modelConfigs: {
                    temperature: 0.2,
                },
                apiKey: '********************************',
                awsRegion: 'us-east-1',
            },
        },
    ],
} as const;

@Component({
    selector: 'cor-run-job-details',
    templateUrl: './run-job-details.component.html',
    styleUrls: ['./run-job-details.component.scss'],
    imports: [
        ReactiveFormsModule,
        FormsModule,
        RouterModule,
        InputContainerComponent,
        CodeMirrorComponent,
        TooltipModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        TextareaModule,
        RadioButtonModule,
        PanelModule,
        SelectModule,
        CardModule,
        InputNumberModule,
        DefinitionComponent,
        CustomHotTableComponent,
        FieldErrorComponent,
        InputSelectComponent,
        IncludesPipe,
        SkeletonComponent,
        AddMoreComponent,
        SelectModule,
        SelectButtonModule,
        StatusIconComponent,
        LocalDatePipe,
        TimeTakenPipe,
        JobRuntimeInfoComponent,
        JoinPipe,
        TruncatePipe,
        InputEditorComponent,
        JsonEditorComponent,
        SkeletonLoaderComponent,
        DiffViewerComponent,
        PromptDefinitionComponent,
        DescriptionComponent,
        DialogBoxComponent,
        SplitterModule,
        ListboxModule,
        pDataView,
        MessageModule,
        NoDataComponent,
        DatePickerModule,
        ButtonModule,
        PopoverModule,
        FindItemPipe,
        NgClass,
        NgStyle,
        NgTemplateOutlet,
        TagModule,
        DynamicFieldComponent,
    ],
})
export class RunJobDetailsComponent implements OnInit {
    // URL Params
    protected readonly currentProject = injectParams<string>('projectSlug');

    // Query Params
    readonly _entityIdParam = linkedQueryParam('entityId', {
        parse: paramToNumber(),
        stringify: value => value?.toString(),
    });
    readonly _jobIdParam = linkedQueryParam('jobId', {
        parse: paramToNumber(),
        stringify: value => value?.toString(),
    });
    readonly _submissionDateParam = linkedQueryParam('submissionDate');
    readonly _executionTypeParam = linkedQueryParam<EXECUTION_TYPE>('type');
    readonly _baseHillclimbSimIdParam = linkedQueryParam('baseHillclimbSimId', {
        parse: paramToNumber(),
        stringify: value => value?.toString(),
    });

    /**
     * Specifies the id of the CURRENT object in this job
     * For example: The model_version_id if this is a model simulation
     */
    readonly entityId = model<number>(null);
    /**
     * In the rerun case:
     *   Specifies the simulation id incase of Re-run simulation from job history
     *   Used to fetch the simulation details and pre-populate to the simulation form.
     * In the view case:
     *   Specifies the jobID that he user is trying to view
     * In the run new case:
     *   Will be null
     */
    readonly jobId = model<number>(null);
    readonly rerunReports = model<boolean>(false);
    readonly reportId = model<number>(null);
    /**
     * Specifies if the form is in rerun mode
     * - The default false is used when the component is loaded from a URL
     * This is an input to the component and used to set the viewType. Inside the component, use viewType, For example:
     *     viewType() === VIEW_TYPE.EXISTING
     * instead of this variable
     */
    readonly isExisitingJob = input(false);
    onRunJob = output<{ option: EVENT_TYPE }>();
    showLogsEvent = output<void>(); // Emits navigation to JobResult tab which has Error Log
    updateJob = output<JobModel>(); // Emits updating the job to parent component

    protected readonly OBJECTS = OBJECTS;
    protected readonly GGX_GLOBAL_OBJECTS = GGX_GLOBAL_OBJECTS;
    protected readonly OBJECT_DISPLAY_NAMES = OBJECT_DISPLAY_NAMES;
    protected readonly REPORT_PARAMETER_TYPE = REPORT_PARAMETER_TYPE;
    protected readonly SKELETON_TYPES = SKELETON_TYPES;
    protected readonly MODEL_INPUT_TYPE = MODEL_INPUT_TYPE;
    protected readonly EXECUTION_TYPE = EXECUTION_TYPE;
    protected readonly JOB_SAMPLE_TYPES = JOB_SAMPLE_TYPES;
    protected readonly EVENT_TYPE = EVENT_TYPE;
    protected readonly VIEW_TYPE = VIEW_TYPE;
    protected readonly SCHEDULED_JOB_STATUS = SCHEDULED_JOB_STATUS;
    protected readonly REGISTERED_TABLES_DATASOURCE = REGISTERED_TABLES_DATASOURCE;
    protected readonly CUSTOM_DATA_DATASOURCE = CUSTOM_DATA_DATASOURCE;
    protected readonly AI_GENERATED_DATASOURCE = AI_GENERATED_DATASOURCE;
    protected readonly ANNOTATION_QUEUE_DATASOURCE = ANNOTATION_QUEUE_DATASOURCE;
    protected readonly RISK_TYPES = RISK_TYPES;
    protected readonly TASK_TYPES = TASK_TYPES;
    protected readonly PIPELINE_TYPE = PIPELINE_TYPE;
    protected readonly EVALUATION_TYPE = EVALUATION_TYPE;
    protected readonly maxUploadSize = computed(() =>
        Helpers.megabytesToBytes(this.platformSettingService.settings()?.maxContentLength),
    );
    protected readonly TESTER_LLM_CONFIG_SCHEMA = TESTER_LLM_CONFIG_SCHEMA;

    private readonly inputService = inject(InputService);
    private readonly toast = inject(ToastNotificationService);
    private readonly attachmentService = inject(AttachmentService);
    private readonly platformSettingService = inject(PlatformSettingService);
    private readonly injector = inject(Injector);
    private readonly destroyRef = inject(DestroyRef);
    private readonly windowService = inject(WindowService);
    private readonly inferenceService = inject(InferenceService);
    protected readonly permissionService = inject(PermissionService);
    protected readonly simulationService = inject(SimulationService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute) as NgActivatedRoute<
        Data,
        Params,
        {
            entityId: number;
            object: OBJECTS;
            jobId: number;
            submissionDate: string;
            type: EXECUTION_TYPE;
            baseHillclimbSimId?: number;
            reportId?: number;
        }
    >;
    protected readonly currentUser = inject(AuthenticationService).currentUser;
    private readonly promptService = inject(PromptService);
    private readonly reportService = inject(ReportsService);

    readonly object = signal(this.route.snapshot.data.object as OBJECTS); //Specifies the object using this component.
    readonly showHeaderSection = signal(false);
    readonly baseHillclimbSimId = signal<number>(null);
    protected readonly isJobDataLoaded = signal(false);
    protected readonly jobData = signal<JobModel>(null);
    protected readonly runningJob = signal<EVENT_TYPE>(null);
    protected readonly recurrenceDates = signal<string[]>([]);
    readonly recurrenceOptions = signal<ReturnType<(typeof JobModel)['getJobSchedulingOptions']>>(
        [],
    );
    protected readonly pipelineType = signal<PIPELINE_TYPE>(null);

    protected loadingState = {
        isCurrentObjectDataLoading: signal(false),
        promptLoading: signal(false), // Only used for hill climbing jobs
        reportParametersLoading: computed(() =>
            this.dataLogicState().every(item => item.reportParametersListLoading()),
        ),
        reportMetricsLoading: computed(() =>
            this.dataLogicState().every(item => item.metricThresholdsListLoading()),
        ),
    };

    protected readonly inputDataSources = signal<{
        reportList: (InputResponseModel & { url: string })[];
    }>({
        reportList: [],
    });

    protected hillClimbing = {
        promptList: signal<PromptAdapter[]>([]),
        selectedPromptId: signal<number>(null),
        promptDefinitionStore: signal<Record<number, string>>(null),
    };

    /**
     * Specifies the service of the object using the simulation.
     */
    service: WithCrudService<any> &
        WithJobService &
        Partial<WithExportableService> &
        WithLineageService;

    form: JobFormGroup;

    constructor() {
        combineLatest({
            entityId: toObservable(this._entityIdParam),
            jobId: toObservable(this._jobIdParam),
            submissionDate: toObservable(this._submissionDateParam),
            executionType: toObservable(this._executionTypeParam),
            baseHillclimbSimId: toObservable(this._baseHillclimbSimIdParam),
        })
            .pipe(auditTime(0), takeUntilDestroyed())
            .subscribe(() => {
                this.loadFromQueryParams();
            });
    }

    protected dataLogicInfo = {
        inputTablesList: signal<InputResponseModel[]>([]), // Used in Data Logic
        inputAnnotationQueuesList: signal<InputResponseModel[]>([]), // Used in Data Logic
        inputResourcesList: signal<InputResponseModel[]>([]), // Used in Data Logic
        // Used to provide intellisense for tables in the Data Logic definitions
        definitionInputs: computed((): InputResponseModel[] => [
            ...this.dataLogicInfo.inputTablesList(),
            ...this.dataLogicInfo.inputResourcesList(),
            ...this.dataLogicInfo.inputAnnotationQueuesList(),
        ]),
    };

    // The default value is taken when there is no report selected
    protected readonly dataLogicState = signal<
        {
            reportDetails: WritableSignal<ReportAdapter>;
            selectedReportDataSource: WritableSignal<ReportDataSource>;
            reportParametersListLoading: WritableSignal<boolean>;
            metricThresholdsListLoading: WritableSignal<boolean>;
            hasOptionalParameters: WritableSignal<boolean>; // Whether to show the collapse/expand buttonf or optional params
            showOptionalParameters: WritableSignal<boolean>; // Whether the optional params is collapsed
            parameterColumns: WritableSignal<Partial<ReportParameterAdapter>[]>; // Show the columns needed in data
            parameterFields: WritableSignal<Record<string, DynamicFormField>>;
            customSuggestions: WritableSignal<CodeSuggestionItem[]>; // For code mirror suggestions
            showResourceInput: WritableSignal<boolean>; // Whether to show the resource input
            // The arguments that should be returned from Data Logic - from  /simulations/options/column
            expectedArguments: WritableSignal<
                {
                    alias: string;
                    type: string;
                    isMandatory: boolean;
                    description?: string;
                    default: { object: OBJECTS; value: string }[];
                    objects?: string[]; // The objects where this argument is needed (current + challengers)
                }[]
            >;
        }[]
    >([]);

    protected cachedData = {
        simulationId: signal<number>(null),
        existingJobReportParamsRx: resource({
            params: (): { viewType: VIEW_TYPE; simId: number } => ({
                viewType: this.viewType(),
                simId: this.cachedData.simulationId(),
            }),
            loader: ({ params }) => {
                if (params.viewType !== VIEW_TYPE.EXISTING || isNil(params.simId)) {
                    return null;
                }
                return lastValueFrom(
                    this.simulationService
                        .getReportParametersList({
                            possible: false,
                            simulationId: params.simId,
                            evalId: null,
                            reportIds: [],
                        })
                        .pipe(map(({ result }) => result)),
                );
            },
        }).asReadonly(),
        existingJobMetricsThresholdRx: resource({
            params: (): { viewType: VIEW_TYPE; simId: number } => ({
                viewType: this.viewType(),
                simId: this.cachedData.simulationId(),
            }),
            loader: ({ params }) => {
                if (params.viewType !== VIEW_TYPE.EXISTING || isNil(params.simId)) {
                    return null;
                }
                return lastValueFrom(
                    this.simulationService
                        .getMetricThresholdsList({
                            possible: false,
                            simId: params.simId,
                            reportIds: [], // unused for possible=false
                        })
                        .pipe(map(({ result }) => result)),
                );
            },
        }).asReadonly(),
    };

    protected readonly viewType = computed(() => {
        if (this.isExisitingJob()) {
            return VIEW_TYPE.EXISTING;
        }

        if (this.rerunReports()) {
            return VIEW_TYPE.RERUN_REPORTS;
        }

        if (this.jobId()) {
            return VIEW_TYPE.RERUN;
        }

        return VIEW_TYPE.NEW;
    });

    protected readonly defaultDataSources = computed(() => {
        return [
            ...DEFAULT_DATASOURCES,
            ...(this.object() === OBJECTS.MONITORING ? [ANNOTATION_QUEUE_DATASOURCE] : []),
        ].map(ds => ({
            ...ds,
            disabled:
                !this.permissionService.currentProjectPermissions().hasDataUploadAccess &&
                ds.id === CUSTOM_DATA_DATASOURCE.id,
        }));
    });

    // AI Data Generation Modal
    protected aiDataGenerationModal = {
        isOpen: signal(false),
        currentDataLogicIndex: signal<number>(null),
        form: new FormGroup({
            reportDescription: new FormControl(''),
            objectDescription: new FormControl(''),
            additionalContext: new FormControl(''),
        }),
    };

    // Report selection popup
    protected reportDialog = {
        isOpen: signal(false),
        currentDataLogicIndex: signal<number>(null),
        searchString: signal<string>(''),
        filters: {
            project: {
                dataField: 'projectSlug',
                list: signal<{ name: string; displayName: string; count: number }[]>([]),
                selectedItems: signal<string[]>([]),
                hasSelected: computed(
                    (): boolean => this.reportDialog.filters.project.selectedItems().length > 0,
                ),
                isExpanded: signal(true),
            },
            riskType: {
                dataField: 'riskType',
                list: signal(
                    Object.entries(RISK_TYPES)
                        .map(([name, riskType]) => ({
                            name,
                            displayName: riskType.displayName,
                            count: 0,
                        }))
                        .filter(x => x.name !== 'other'),
                ),
                selectedItems: signal<string[]>([]),
                hasSelected: computed(
                    (): boolean => this.reportDialog.filters.riskType.selectedItems().length > 0,
                ),
                isExpanded: signal(true),
            },
            taskType: {
                isVisible: computed(() => this.object() === OBJECTS.REPORT),
                dataField: 'taskType',
                list: signal(
                    Object.entries(TASK_TYPES)
                        .map(([name, taskType]) => ({
                            name,
                            displayName: taskType.displayName,
                            count: 0,
                        }))
                        .filter(x => x.name !== 'other'),
                ),
                selectedItems: signal<string[]>([]),
                hasSelected: computed(
                    (): boolean => this.reportDialog.filters.taskType.selectedItems().length > 0,
                ),
                isExpanded: signal(true),
            },
            riskDomain: {
                isVisible: computed(() => this.object() === OBJECTS.REPORT),
                dataField: 'riskDomain',
                list: signal(
                    Object.entries(RISK_DOMAINS)
                        .map(([name, riskDomain]) => ({
                            name,
                            displayName: riskDomain.displayName,
                            count: 0,
                        }))
                        .filter(x => x.name !== 'other'),
                ),
                selectedItems: signal<string[]>([]),
                hasSelected: computed(
                    (): boolean => this.reportDialog.filters.riskDomain.selectedItems().length > 0,
                ),
                isExpanded: signal(false),
            },
            evaluationMethod: {
                isVisible: computed(() => this.object() === OBJECTS.REPORT),
                dataField: 'evaluationMethodology',
                list: signal(
                    Object.entries(EVALUATION_METHODS)
                        .map(([name, evaluationMethod]) => ({
                            name,
                            displayName: evaluationMethod.displayName,
                            count: 0,
                        }))
                        .filter(x => x.name !== 'other'),
                ),
                selectedItems: signal<string[]>([]),
                hasSelected: computed(
                    (): boolean =>
                        this.reportDialog.filters.evaluationMethod.selectedItems().length > 0,
                ),
                isExpanded: signal(true),
            },
            status: {
                isVisible: computed(() => WORKFLOWABLE_OBJECTS.includes(this.object())),
                dataField: 'currentStatus',
                list: signal(
                    Object.values(WORKFLOW_STATUS)
                        .filter(status => status !== WORKFLOW_STATUS.ACTIVE)
                        .map(status => ({
                            name: status,
                            displayName: status,
                            count: 0,
                        })),
                ),
                selectedItems: signal<string[]>([]),
                hasSelected: computed(
                    (): boolean => this.reportDialog.filters.status.selectedItems().length > 0,
                ),
                isExpanded: signal(false),
            },
        },
        hasAnyFilters: computed(
            (): boolean =>
                Object.values(this.reportDialog.filters).some(filterItem =>
                    filterItem.hasSelected(),
                ) || !!this.reportDialog.searchString(),
        ),
    };

    protected readonly filteredReports = computed(() => {
        const reports = this.inputDataSources().reportList;
        const { filters, searchString } = this.reportDialog;
        const search = searchString().toLowerCase().trim();

        // Collect selected filter values
        const selectedFilters = {
            project: filters.project.selectedItems(),
            riskType: filters.riskType.selectedItems(),
            taskType: filters.taskType.selectedItems(),
            riskDomain: filters.riskDomain.selectedItems(),
            evaluationMethod: filters.evaluationMethod.selectedItems(),
            status: filters.status.selectedItems(),
        };

        // Apply filtering logic
        return reports.filter(report => {
            const matchesProject =
                selectedFilters.project.length === 0 ||
                filters.project.selectedItems().includes('all_projects') ||
                selectedFilters.project.includes(report.projectSlug);
            const matchesRiskType =
                selectedFilters.riskType.length === 0 ||
                selectedFilters.riskType.includes(report.riskType);
            const matchesTaskType =
                selectedFilters.taskType.length === 0 ||
                selectedFilters.taskType.includes(report.taskType);
            const matchesRiskDomain =
                selectedFilters.riskDomain.length === 0 ||
                selectedFilters.riskDomain.includes(report.riskDomain);
            const matchesEvaluationMethod =
                selectedFilters.evaluationMethod.length === 0 ||
                selectedFilters.evaluationMethod.includes(report.evaluationMethodology);
            const matchesStatus =
                selectedFilters.status.length === 0 ||
                selectedFilters.status.includes(report.currentStatus);
            const matchesSearch =
                !search ||
                report.name?.toLowerCase().includes(search) ||
                report.description?.toLowerCase().includes(search) ||
                report.riskType?.toLowerCase().includes(search) ||
                report.taskType?.toLowerCase().includes(search) ||
                report.riskDomain?.toLowerCase().includes(search) ||
                report.evaluationMethodology?.toLowerCase().includes(search);

            return (
                matchesProject &&
                matchesRiskType &&
                matchesTaskType &&
                matchesRiskDomain &&
                matchesEvaluationMethod &&
                matchesStatus &&
                matchesSearch
            );
        });
    });

    protected readonly isGeneratingAiData = signal(false);

    protected readonly showAdditionalOptions = signal(false);

    ngOnInit() {
        this.service = this.injector.get<any>(OBJECT_SERVICE_MAPPER[this.object()]);
        this.showHeaderSection.set(this.router.url.includes('/run-job'));
        if (this.viewType() !== VIEW_TYPE.EXISTING) {
            // For new & rerun jobs that the user is trying to run - we fetch this info from queryParams
            // For existing case - it is passed as an input to this component from the job-dashboard component
            this.entityId.set(this._entityIdParam());
            this.jobId.set(this._jobIdParam());
            this.baseHillclimbSimId.set(this._baseHillclimbSimIdParam() || null);
        }
    }

    private loadFromQueryParams() {
        // The Resources (like GF, Prompt, etc.) & Input Tables that can be used in DataLogic
        const dataLogicInputs$ = iif(
            () => GGX_GLOBAL_OBJECTS.includes(this.object()),
            this.inputService.getInputs({
                case: INPUT_CASES.DATA_LOGIC_INPUTS,
                simulationId: this.jobId() || null,
                entityType: this.object(),
            }),
            of(null),
        );
        // The Reports that can be used in DataLogic
        const dataLogicReports$ = iif(
            () => GGX_GLOBAL_OBJECTS.includes(this.object()),
            this.inputService.getInputs({
                case: INPUT_CASES.SIMULATION_REPORT_INPUTS,
                simulationId: [VIEW_TYPE.EXISTING, VIEW_TYPE.RERUN_REPORTS].includes(
                    this.viewType(),
                )
                    ? this.jobId()
                    : null,
                entityType: this.object(),
                entityId: this.entityId(),
                existing: [VIEW_TYPE.EXISTING, VIEW_TYPE.RERUN_REPORTS].includes(this.viewType()),
                possible: ![VIEW_TYPE.EXISTING, VIEW_TYPE.RERUN_REPORTS].includes(this.viewType()),
            }),
            of(null),
        );

        const simulation$ = iif(
            () => !!this.jobId(),
            defer(() =>
                this.simulationService
                    .getSimulation(this.jobId())
                    .pipe(map(({ result }) => result)),
            ),
            of(
                JobModel.getForAPI(
                    JobModel.createFromAPI({
                        submissionDate: this._submissionDateParam(),
                        executionType: this._executionTypeParam(),
                    } as JobAdapter),
                    this.object(),
                ),
            ), // Emit null if condition isn't met
        );
        const pipeline$ = iif(
            () => this.object() === OBJECTS.PIPELINE && !!this.entityId(),
            defer(() =>
                this.service
                    .getById(this.entityId(), { simple: true })
                    .pipe(map(({ result }) => result)),
            ),
            of(null),
        );

        forkJoin({
            dataLogicInputs: dataLogicInputs$,
            dataLogicReports: dataLogicReports$,
            simulation: simulation$,
            pipeline: pipeline$,
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ dataLogicInputs, dataLogicReports, simulation, pipeline }) => {
                this.jobData.set(JobModel.createFromAPI(simulation));
                this.pipelineType.set(pipeline?.pipelineType);

                if (GGX_GLOBAL_OBJECTS.includes(this.object())) {
                    this.dataLogicInfo.inputTablesList.set(
                        dataLogicInputs.result.filter(x => x.objectType === OBJECTS.DATA_TABLE),
                    );
                    this.dataLogicInfo.inputResourcesList.set(
                        dataLogicInputs.result.filter(
                            x =>
                                x.objectType !== OBJECTS.DATA_TABLE &&
                                x.objectType !== OBJECTS.ANNOTATION_QUEUE,
                        ),
                    );
                    this.onReportsLoad(dataLogicReports.result);
                    if (this.object() === OBJECTS.MONITORING) {
                        this.dataLogicInfo.inputAnnotationQueuesList.set(
                            dataLogicInputs.result.filter(
                                x => x.objectType === OBJECTS.ANNOTATION_QUEUE,
                            ),
                        );
                    }
                }

                this.form = JobModel.getForm(
                    {
                        ...this.jobData(),
                        // In case of Hill climb on different prompt, update the execution type to Hill Climbing
                        executionType:
                            this.viewType() === VIEW_TYPE.RERUN &&
                            this._executionTypeParam() === EXECUTION_TYPE.HILL_CLIMBING
                                ? EXECUTION_TYPE.HILL_CLIMBING
                                : this.jobData()?.executionType,
                    },
                    this.object(),
                    this.entityId(),
                    this.viewType(),
                    this.dataLogicInfo.inputResourcesList(),
                );
                if (
                    this.viewType() === VIEW_TYPE.RERUN &&
                    this._executionTypeParam() === EXECUTION_TYPE.HILL_CLIMBING &&
                    this.form.controls.hillClimbingPromptAssets.length > 0
                ) {
                    // We only expect one prompt to be changed in Hillclimbing today.
                    // So select the first prompt id
                    this.hillClimbing.selectedPromptId.set(
                        this.form.controls.hillClimbingPromptAssets.controls[0].controls.entityId
                            .value,
                    );
                }
                this.cachedData.simulationId.set(this.form.controls.id.value);
                this.form.controls.id.valueChanges
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(id => this.cachedData.simulationId.set(id));

                // Fetch the prompts for the current object and allows user to view/change the definition
                if (
                    this.form.controls.executionType.value === EXECUTION_TYPE.HILL_CLIMBING &&
                    this.object() === OBJECTS.PIPELINE
                ) {
                    this.fetchCurrentObjectLineage();
                }

                if (GGX_GLOBAL_OBJECTS.includes(this.object())) {
                    // Set initial signal array for the dataLogic state
                    this.dataLogicState.set(
                        this.form.controls.dataLogicFormArray.controls.map(dataLogicForm => ({
                            // If no dataSource is selected - we use User registered tables as default
                            selectedReportDataSource: signal(REGISTERED_TABLES_DATASOURCE),
                            reportDetails: signal<ReportAdapter>(null),
                            reportParametersListLoading: signal(false),
                            metricThresholdsListLoading: signal(false),
                            hasOptionalParameters: signal(
                                dataLogicForm.controls.reportParameters.value.some(
                                    p => !p.isMandatory && !p.value,
                                ),
                            ),
                            showOptionalParameters: signal(false),
                            parameterColumns: signal(
                                dataLogicForm.controls.reportParameters.value.filter(
                                    p => p.type === REPORT_PARAMETER_TYPE.SINGLE_OBJECT,
                                ),
                            ),
                            parameterFields: signal({}),
                            customSuggestions: signal([]),
                            showResourceInput: signal(
                                dataLogicForm.controls.inputFeatureVersionIds.value.length > 0,
                            ),
                            expectedArguments: signal([]),
                        })),
                    );
                    this.form.controls.dataLogicFormArray.controls.forEach(dataLogicForm => {
                        this.subscribeToSimulationTypeChanges(dataLogicForm);
                        this.subscribeToDataLogicChanges(dataLogicForm);
                        this.subscribeToDashboardReportsChanges(dataLogicForm);
                        this.initializeDataSourceTable(
                            this.form.controls.dataLogicFormArray.controls.indexOf(dataLogicForm),
                        );
                    });

                    // If object is report then for all the datalogic set the report id
                    if (this.object() === OBJECTS.REPORT) {
                        this.form.controls.dataLogicFormArray.controls.forEach(dataLogicForm => {
                            dataLogicForm.controls.reportVersionId.setValue(this.entityId());
                        });
                    } else if (this.reportId() && this.viewType() === VIEW_TYPE.NEW) {
                        this.form.controls.dataLogicFormArray.controls.forEach(dataLogicForm => {
                            dataLogicForm.controls.reportVersionId.setValue(
                                toNumber(this.reportId()),
                            );
                        });
                    }
                }

                // get object detail to populate object name
                this.loadingState.isCurrentObjectDataLoading.set(true);

                const entityId =
                    this.form.controls.inputsFormGroup.controls.currentObjectFormGroup.controls.entityId.getRawValue();
                this.service
                    .getById(entityId)
                    .subscribe(response => {
                        this.form.controls.inputsFormGroup.controls.currentObjectFormGroup.controls.currentObjectName.setValue(
                            response.result.name,
                            { onlySelf: true, emitEvent: false },
                        );
                    })
                    .add(() => this.loadingState.isCurrentObjectDataLoading.set(false));

                this.mapObjectFilterWithReportFilter();
                if (this.viewType() === VIEW_TYPE.EXISTING) {
                    this.form.disable({ emitEvent: false });
                }
            })
            .add(() => {
                this.isJobDataLoaded.set(true);
                this.subscribeToRecurrencyChanges();

                // Initialize recurrence options with the job's scheduled date or current date
                const scheduledDate = this.jobData()?.scheduledDate || new Date();
                const dateString =
                    scheduledDate instanceof Date ? scheduledDate.toISOString() : scheduledDate;
                this.recurrenceOptions.set(JobModel.getJobSchedulingOptions(dateString));

                // Patch the frequency form control with existing data
                this.patchFrequencyFormControl();

                // Set recurrence dates if frequency is already set
                const frequency = this.form.controls.schedulingFormGroup.controls.frequency.value;
                if (frequency?.cycle) {
                    this.setRecurrenceDates(frequency.cycle, frequency.day);
                }
            });
    }

    private mapObjectFilterWithReportFilter() {
        // TODO: we are only doing a 1-1 mapping from Report dimensions to object dimensions here
        // We should improve this heuristic over time to make things more intuitive. It is not always the case
        // that report dimensions have a 1-1 mapping to object dimensions
        this.service
            .getById(this.entityId())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ result }) => {
                if (!result) return;

                Object.entries(this.reportDialog.filters).forEach(
                    ([, filterItem]: [string, any]) => {
                        if (filterItem.dataField === 'currentStatus') return;

                        const filterValueOfObject = result[filterItem.dataField];
                        if (!filterValueOfObject) return;

                        // Set selectedItems directly based on object's filter value
                        const selectedNames = Array.isArray(filterValueOfObject)
                            ? filterValueOfObject
                            : [filterValueOfObject];
                        filterItem.selectedItems.set(selectedNames);

                        if (filterItem.selectedItems().length > 0) {
                            filterItem.isExpanded.set(true);
                        }
                    },
                );
            });
    }

    protected addHillClimbingPrompt(prompt: PromptAdapter) {
        // Show the prompt template used while creating the job for existing and rerun
        // For New job, use the original template registered on the prompt
        // For Rerun job, use the previously entered template from jobAssets
        const existingPrompt = JobModel.getHillClimbPromptAssets(this.jobData()).find(
            x => x.entityId === prompt.id,
        );

        // Create a new form group for this prompt
        const newPromptGroup = JobModel.getPromptTemplateFormGroup({
            entityId: prompt.id,
            entityName: prompt.name,
            promptTemplate: existingPrompt?.promptTemplate || prompt?.promptTemplate,
        });

        // Push into existing FormArray
        this.form.controls.hillClimbingPromptAssets.push(newPromptGroup);
    }

    protected removeHillClimbingPrompt(promptId: number) {
        const formArray = this.form.controls.hillClimbingPromptAssets;
        const index = formArray.controls.findIndex(control => control.value.entityId === promptId);

        if (index !== -1) {
            formArray.removeAt(index);
        }
    }

    private fetchCurrentObjectLineage() {
        // For New Job, get the definition from the job lineage
        // For Job details, get the definition from the objects transaction id and show in the left
        // For Rerun, baseline should be taken from object registry definition and current definition from simulationData

        this.loadingState.promptLoading.set(true);
        // Fetch lineage data and filter relevant nodes
        this.service
            .getLineageData(this.entityId(), {
                case: LINEAGE_CASES.CHILDREN,
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(response => {
                    const lineagePrompts = Object.values(response.result.nodes).filter(
                        node => node.objectType === OBJECTS.PROMPT,
                    );
                    if (lineagePrompts.length === 0) return of([] as PromptAdapter[]);

                    // Fetch prompt data for filtered nodes
                    // FIXME: Here we assume user has access to all the prompts
                    return this.promptService
                        .getAll({ ids: lineagePrompts.map(node => node.id) })
                        .pipe(
                            takeUntilDestroyed(this.destroyRef),
                            map(promptResponse => promptResponse.result),
                        );
                }),
            )
            .subscribe(promptList => {
                this.hillClimbing.promptList.set(promptList);
                if (promptList.length > 0) {
                    this.hillClimbing.selectedPromptId.set(promptList[0].id);
                }
                const promptRecord = promptList.reduce<Record<number, string>>((acc, p) => {
                    acc[p.id] = p.promptTemplate;

                    return acc;
                }, {});

                this.hillClimbing.promptDefinitionStore.set(promptRecord);
            })
            .add(() => this.loadingState.promptLoading.set(false));
    }

    protected openPromptInNewTab(promptId: number, event: MouseEvent) {
        const url = RouterHelper.buildRoute({
            module: MODULES.ASSETS,
            object: OBJECTS.PROMPT,
            id: promptId,
        });
        this.windowService.openInNewTab(url).focus();

        // Avoid opening accordion panel on button click
        event.stopPropagation();
    }

    protected async resetPrompt(promptId: number, index: number, event: MouseEvent) {
        const { result } = await lastValueFrom(this.promptService.getById(promptId));
        this.form.controls.hillClimbingPromptAssets.at(index).patchValue({
            promptTemplate: result.promptTemplate,
        });
        // Avoid opening accordion panel on button click
        event.stopPropagation();
    }

    protected onReportsLoad(reports: InputResponseModel[]) {
        this.inputDataSources.set({
            ...this.inputDataSources(),
            reportList: reports.map(report => {
                return {
                    ...report,
                    url: RouterHelper.buildRoute(
                        Helpers.convertInputResponseModelToUIRouteSchema(report),
                    ),
                };
            }),
        });

        this.reportDialog.filters.project.list.set([
            {
                name: 'all_projects',
                displayName: 'All Projects',
                count: reports.length,
            },
            {
                name: this.currentProject(),
                displayName: `Current Project (${this.currentProject()})`,
                count: reports.filter(y => y.projectSlug === this.currentProject()).length,
            },
        ]);

        // Set count for report filters
        Object.entries(this.reportDialog.filters).forEach(
            ([filterName, filterItem]: [string, any]) => {
                if (filterName === 'project') return;
                filterItem.list.update((items: any[]) =>
                    items.map((x: any) => ({
                        ...x,
                        count: reports.filter(
                            y => y[filterItem.dataField as keyof InputResponseModel] === x.name,
                        ).length,
                    })),
                );
            },
        );
    }

    private subscribeToDashboardReportsChanges(
        dataLogicForm: ArrayItem<JobFormGroup['controls']['dataLogicFormArray']['controls']>,
    ): void {
        dataLogicForm.controls.reportVersionId.valueChanges
            .pipe(
                startWith(dataLogicForm.controls.reportVersionId.value),
                distinctUntilChanged(),
                // Fetch info about the report that was selected to initialize other parts
                switchMap(reportId =>
                    reportId ? this.reportService.getById(reportId) : of({ result: null }),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ result }) => {
                const dataLogicIndex =
                    this.form.controls.dataLogicFormArray.controls.indexOf(dataLogicForm);
                this.dataLogicState()[dataLogicIndex].reportDetails.set(result);

                this.initializeReportDataSourceExamples(dataLogicForm);
                this.fetchAndInitializeReportParameters(dataLogicForm);
                this.fetchAndInitializeMetricThresholds(dataLogicForm);
            });
    }

    private initializeReportDataSourceExamples(
        dataLogicForm: ArrayItem<JobFormGroup['controls']['dataLogicFormArray']['controls']>,
    ) {
        const dataLogicIndex =
            this.form.controls.dataLogicFormArray.controls.indexOf(dataLogicForm);
        const examples =
            this.dataLogicState()[dataLogicIndex].reportDetails()?.dataLogicExamples ||
            this.defaultDataSources();
        // NOTE: For Existing job or rerun we set the selected option based on inputType and data
        if (this.viewType() === VIEW_TYPE.RERUN || this.viewType() === VIEW_TYPE.EXISTING) {
            // Check if annotation queues are being used
            if (
                dataLogicForm.controls.inputType.value === MODEL_INPUT_TYPE.PYTHON_FUNCTION &&
                dataLogicForm.controls.annotationQueueId?.value
            ) {
                this.dataLogicState()[dataLogicIndex].selectedReportDataSource.set(
                    ANNOTATION_QUEUE_DATASOURCE,
                );
            } else if (
                dataLogicForm.controls.inputType.value === MODEL_INPUT_TYPE.PYTHON_FUNCTION &&
                !dataLogicForm.controls.annotationQueueId?.value
            ) {
                this.dataLogicState()[dataLogicIndex].selectedReportDataSource.set(
                    REGISTERED_TABLES_DATASOURCE,
                );
            } else if (dataLogicForm.controls.inputType.value === MODEL_INPUT_TYPE.DATASOURCE) {
                this.dataLogicState()[dataLogicIndex].selectedReportDataSource.set(
                    CUSTOM_DATA_DATASOURCE,
                );
            }
        } else if (this.viewType() === VIEW_TYPE.RERUN_REPORTS) {
            dataLogicForm.controls.reportVersionId.disable();
            dataLogicForm.controls.tableIds.disable();
            dataLogicForm.controls.inputType.disable();
            dataLogicForm.controls.inputFeatureVersionIds.disable();
            dataLogicForm.controls.definition.disable();
        } else if (this.viewType() === VIEW_TYPE.NEW) {
            // If running a new job always select the 1st option as default
            this.dataLogicState()[dataLogicIndex].selectedReportDataSource.set(examples[0]);
            this.changeReportDataSource(dataLogicForm, examples[0].id);
        }
    }

    protected changeReportDataSource(
        dataLogicForm: ArrayItem<JobFormGroup['controls']['dataLogicFormArray']['controls']>,
        exampleId: number,
    ) {
        const dataLogicIndex =
            this.form.controls.dataLogicFormArray.controls.indexOf(dataLogicForm);
        const example = [
            ...(this.dataLogicState()[dataLogicIndex].reportDetails()?.dataLogicExamples || []),
            ...this.defaultDataSources(),
        ].find(dataSource => dataSource.id === exampleId);
        if (!example) return;

        this.dataLogicState()[dataLogicIndex].selectedReportDataSource.set(example);

        let inputType: MODEL_INPUT_TYPE;
        // Example id > 0 are the example datasource provided from the report
        // Example id < 0 are the corridor datasource option - ["User Registered Tables", "Custom Data"]
        if (example?.id === REGISTERED_TABLES_DATASOURCE.id) {
            inputType = MODEL_INPUT_TYPE.PYTHON_FUNCTION;
        } else if (example?.id === CUSTOM_DATA_DATASOURCE.id) {
            inputType = MODEL_INPUT_TYPE.DATASOURCE;
        } else if (example?.id === AI_GENERATED_DATASOURCE.id) {
            inputType = MODEL_INPUT_TYPE.DATASOURCE;
            const dataLogicIndex =
                this.form.controls.dataLogicFormArray.controls.indexOf(dataLogicForm);
            this.onDataGenerationClick(dataLogicIndex);
        } else if (example?.id === ANNOTATION_QUEUE_DATASOURCE.id) {
            inputType = MODEL_INPUT_TYPE.PYTHON_FUNCTION;
        } else {
            // Get the inputType from the example
            inputType = example.dataFile
                ? MODEL_INPUT_TYPE.DATASOURCE
                : MODEL_INPUT_TYPE.PYTHON_FUNCTION;
        }

        dataLogicForm.patchValue({
            inputType,
            definition: example.definition, // Make a copy so the original example is not mutated
            dataFile: cloneDeep(example.dataFile),
            // FIXME: If the report writer provided an item which is not present in this Input API response
            //        that item will not be shown correctly
            inputFeatureVersionIds: InputService.getUniqueIdForSelectedInputs(
                example,
                this.dataLogicInfo.inputResourcesList(),
            ),
            tableIds: [...example.tableIds], // Make a copy so the original example is not mutated
            annotationQueueId:
                // Clear annotation queues if any other example is chosen
                example.id === ANNOTATION_QUEUE_DATASOURCE.id
                    ? dataLogicForm.controls.annotationQueueId?.value || null
                    : null,
        });
    }

    private fetchAndInitializeReportParameters(
        dataLogicForm: ArrayItem<JobFormGroup['controls']['dataLogicFormArray']['controls']>,
    ) {
        const dataLogicIndex =
            this.form.controls.dataLogicFormArray.controls.indexOf(dataLogicForm);
        if (!dataLogicForm.controls.reportVersionId.value) {
            // If no report chosen => no parameters
            dataLogicForm.removeControl('reportParameters');
            const paramsFormArray = new FormArray([]);
            if (this.form.disabled) {
                paramsFormArray.disable({ emitEvent: false });
            }
            dataLogicForm.addControl('reportParameters', paramsFormArray);

            // Sync the UI state variables: No column params and no optional params - as there is no report.
            this.dataLogicState()[dataLogicIndex].hasOptionalParameters.set(false);
            this.dataLogicState()[dataLogicIndex].parameterColumns.set([]);
            this.updateDataLogicInitColumns(dataLogicIndex);
        } else {
            this.dataLogicState()[dataLogicIndex].reportParametersListLoading.set(true);

            iif(
                () =>
                    this.viewType() === VIEW_TYPE.EXISTING &&
                    this.cachedData.existingJobReportParamsRx.hasValue(),
                defer(
                    (): Observable<ItemResponse<ReportParameterAdapter[]>> =>
                        of({
                            result: this.cachedData.existingJobReportParamsRx
                                .value()
                                .filter(param => param.evalId === dataLogicForm.controls.id.value),
                        }),
                ),
                defer(() =>
                    this.simulationService.getReportParametersList({
                        possible: this.viewType() !== VIEW_TYPE.EXISTING,
                        simulationId: this.form.controls.id.value,
                        evalId: dataLogicForm.controls.id.value,
                        reportIds: [dataLogicForm.controls.reportVersionId.value],
                    }),
                ),
            )
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: response => {
                        // If the param is a SingleObject and the object being simulated is a GLOBAL_OBJECT
                        // Then the single object needs to be provided in DataSource - so, we are skipping to be added
                        // in dependencies
                        const parameters = response.result
                            .filter(x => x.type !== REPORT_PARAMETER_TYPE.SINGLE_OBJECT)
                            .map(param => {
                                const existingParam =
                                    dataLogicForm.controls.reportParameters.value.find(
                                        p => p.alias === param.alias,
                                    );

                                return existingParam
                                    ? { ...param, value: existingParam.value }
                                    : { ...param, value: param.defaultValue ?? null };
                            });
                        this.dataLogicState()[dataLogicIndex].parameterFields.set(
                            JobModel.getDynamicFieldsFromReportParameters(
                                sortBy(
                                    parameters,
                                    [p => !p.isMandatory, 'alias'], // Show mandatory items first
                                ),
                            ),
                        );

                        dataLogicForm.removeControl('reportParameters');
                        const paramsFormArray = new FormArray(
                            sortBy(
                                parameters,
                                [p => !p.isMandatory, 'alias'], // Show mandatory items first
                            ).map(param => {
                                const paramForm = JobModel.getReportParameterFormGroup(param);
                                if (
                                    this.viewType() === VIEW_TYPE.RERUN_REPORTS &&
                                    param.type === REPORT_PARAMETER_TYPE.SINGLE_OBJECT
                                ) {
                                    paramForm.disable();
                                }

                                return paramForm;
                            }),
                        );
                        if (this.form.disabled) {
                            paramsFormArray.disable({ emitEvent: false });
                        }
                        dataLogicForm.addControl('reportParameters', paramsFormArray);

                        this.dataLogicState()[dataLogicIndex].hasOptionalParameters.set(
                            parameters.some(p => !p.isMandatory && !p.value),
                        );

                        // For any parameters that are columns - we need to show as chips above the table area
                        const columnParams = response.result.filter(
                            x => x.type === REPORT_PARAMETER_TYPE.SINGLE_OBJECT,
                        );
                        this.dataLogicState()[dataLogicIndex].parameterColumns.set(
                            sortBy(
                                columnParams,
                                [param => !param.isMandatory, 'alias'], // Show mandatory items first
                            ),
                        );
                        this.updateDataLogicInitColumns(dataLogicIndex);
                    },
                })
                .add(() =>
                    this.dataLogicState()[dataLogicIndex].reportParametersListLoading.set(false),
                );
        }
    }

    private fetchAndInitializeMetricThresholds(
        dataLogicForm: ArrayItem<JobFormGroup['controls']['dataLogicFormArray']['controls']>,
    ) {
        if (!dataLogicForm.controls.reportVersionId.value) {
            dataLogicForm.removeControl('metricThresholds');
            const formArray = new FormArray([]);
            if (this.form.disabled) {
                formArray.disable({ emitEvent: false });
            }

            dataLogicForm.addControl('metricThresholds', formArray);
        } else {
            const dataLogicIndex =
                this.form.controls.dataLogicFormArray.controls.indexOf(dataLogicForm);
            this.dataLogicState()[dataLogicIndex].metricThresholdsListLoading.set(true);

            iif(
                () =>
                    this.viewType() === VIEW_TYPE.EXISTING &&
                    this.cachedData.existingJobMetricsThresholdRx.hasValue(),
                defer(
                    (): Observable<ItemResponse<MetricThreshold[]>> =>
                        of({
                            result: this.cachedData.existingJobMetricsThresholdRx.value(),
                        }),
                ),
                defer(() =>
                    this.simulationService.getMetricThresholdsList({
                        possible: this.viewType() !== VIEW_TYPE.EXISTING,
                        simId: this.form.controls.id.value,
                        reportIds: [dataLogicForm.controls.reportVersionId.value],
                    }),
                ),
            )
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: response => {
                        const metricThresholdsList = response.result.map(mt => {
                            const existingMetric = (
                                dataLogicForm.controls.metricThresholds?.value || []
                            ).find(metricTh => metricTh.name === mt.name);

                            return {
                                name: mt.name,
                                id: mt.id,
                                reportName: mt.reportName,
                                thresholdLower: existingMetric?.thresholdLower,
                                thresholdUpper: existingMetric?.thresholdUpper,
                            };
                        });

                        dataLogicForm.removeControl('metricThresholds');
                        const formArray = new FormArray(
                            metricThresholdsList.map(metricThresholdData =>
                                JobModel.getMetricThresholdFormGroup(metricThresholdData),
                            ) || [],
                        );
                        if (this.form.disabled) {
                            formArray.disable({ emitEvent: false });
                        }
                        dataLogicForm.addControl('metricThresholds', formArray);
                    },
                })
                .add(() =>
                    this.dataLogicState()[dataLogicIndex].metricThresholdsListLoading.set(false),
                );
        }
    }

    private subscribeToSimulationTypeChanges(
        dataLogicForm: ArrayItem<JobFormGroup['controls']['dataLogicFormArray']['controls']>,
    ) {
        dataLogicForm.controls.evaluationType.valueChanges
            .pipe(
                startWith(dataLogicForm.controls.evaluationType.value),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(simType => {
                if (simType === EVALUATION_TYPE.MULTI_TURN) {
                    dataLogicForm.controls.autoChatConfig.addValidators(Validators.required);
                    dataLogicForm.controls.autoChatInstructions.addValidators(Validators.required);
                } else {
                    dataLogicForm.controls.autoChatConfig.clearValidators();
                    dataLogicForm.controls.autoChatInstructions.clearValidators();
                }

                dataLogicForm.controls.autoChatConfig.updateValueAndValidity({
                    emitEvent: false,
                });
                dataLogicForm.controls.autoChatInstructions.updateValueAndValidity({
                    emitEvent: false,
                });

                // The Function Arguments that are needed in the DataLogic

                const dataLogicIndex =
                    this.form.controls.dataLogicFormArray?.controls.indexOf(dataLogicForm);
                if (dataLogicIndex === -1) return;
                //FIXME: Instead of multiple api calls store them as there is only 2 types and reuse
                this.simulationService
                    .getColumns({
                        current: this.entityId(),
                        objectType: this.object(),
                        evaluationType: simType,
                    })
                    .subscribe(dataLogicArguments => {
                        this.dataLogicState()[dataLogicIndex].expectedArguments.set(
                            sortBy(
                                dataLogicArguments.result.arguments,
                                [arg => !arg.isMandatory, 'alias'], // Show mandatory items first
                            ).map(arg => ({ ...arg, objects: arg.default.map(d => d.object) })),
                        );
                        this.updateDataLogicInitColumns(dataLogicIndex);
                    });
            });
    }

    /**
     * On changing sample type if type is sampleSize then size is mandatory
     * If sample type is ratio then ratio is mandatory
     * Add /Remove the controls on the basis of sample type selection
     */
    protected onChangeSampleType(event: SelectChangeEvent): void {
        const previousValue =
            this.form.controls.dataSourceFormGroup.controls.sampleInfo.controls.type.value;
        if (event.value === previousValue) {
            // No change in the value - nothing to be done
            return;
        }

        const selectedType = event.value;
        const formGroup = this.form.controls.dataSourceFormGroup.controls.sampleInfo;

        // Clean up any controls we don't need
        if (selectedType !== JOB_SAMPLE_TYPES.SAMPLE_SIZE) {
            formGroup.removeControl('numSamples');
        }
        if (selectedType !== JOB_SAMPLE_TYPES.SAMPLE_RATIO) {
            formGroup.removeControl('sampleRatio');
        }

        // Add any controls we need for the selected type
        if (selectedType === JOB_SAMPLE_TYPES.SAMPLE_SIZE) {
            formGroup.addControl(
                'numSamples',
                new FormControl({ value: 1000, disabled: this.form.disabled }, Validators.required),
            );
        } else if (selectedType === JOB_SAMPLE_TYPES.SAMPLE_RATIO) {
            formGroup.addControl(
                'sampleRatio',
                new FormControl({ value: 10, disabled: this.form.disabled }, Validators.required),
            );
        }

        formGroup.updateValueAndValidity();
    }

    private createPayloadForSimulation(): JobModel {
        const formData = this.form.getRawValue();

        const descriptionSectionData = formData.descriptionFormGroup;

        const currentObjectData = {
            entityId: formData.inputsFormGroup.currentObjectFormGroup.entityId,
        };

        const dataSourceSectionData = {
            sampleInfo: formData.dataSourceFormGroup?.sampleInfo,
        };
        let dataLogics: JobModel['dataLogics'] = [];
        if (GGX_GLOBAL_OBJECTS.includes(this.object())) {
            dataLogics = formData.dataLogicFormArray.map((datalogic, dataLogicIndex) => {
                let {
                    promptVersionIds,
                    featureVersionIds,
                    foundationModelVersionIds,
                    ragVersionIds,
                } = InputService.getForAPIInputsForGlobalObjects(
                    datalogic.inputFeatureVersionIds,
                    this.dataLogicInfo.inputResourcesList(),
                );
                let { tableIds } = datalogic;
                if (datalogic.inputType === MODEL_INPUT_TYPE.DATASOURCE) {
                    // Clear tableIds and object inputs if a data file is available
                    tableIds = [];
                    promptVersionIds = [];
                    featureVersionIds = [];
                    foundationModelVersionIds = [];
                    ragVersionIds = [];
                }

                // Ensure a newly selected upload is treated as a new attachment.
                if (datalogic.dataFile?.upload) {
                    datalogic.dataFile.id = null;
                }

                const defaultDataLogicName = `Evaluation #${dataLogicIndex + 1}`;

                return {
                    id: datalogic.id,
                    name: datalogic.reportVersionId
                        ? this.inputDataSources().reportList.find(
                              report => report.id === datalogic.reportVersionId,
                          )?.name || defaultDataLogicName
                        : defaultDataLogicName,
                    evaluationType: datalogic.evaluationType,
                    // Selecting the report
                    reportVersionId: datalogic.reportVersionId,
                    // Params and metrics for the report
                    reportParameters: datalogic.reportParameters,
                    metricThresholds: datalogic.metricThresholds,
                    // Data for the report
                    definition: datalogic.definition,
                    dataFile: datalogic.dataFile,
                    dataFileId: datalogic.dataFile?.id || null,
                    featureVersionIds,
                    tableIds,
                    annotationQueueId: datalogic.annotationQueueId,
                    aqDateRange: datalogic.aqDateRange,
                    foundationModelVersionIds,
                    promptVersionIds,
                    ragVersionIds,
                    figures: [] as SimulationFigure[],
                    metrics: [] as SimulationMetric[],
                    autoChatConfig: datalogic.autoChatConfig,
                    autoChatInstructions: datalogic.autoChatInstructions,
                };
            });
        }

        // Add scheduling data for Monitoring jobs
        let scheduledDate: Date | null = null;
        let recurrence: string = 'none';
        let recurringData: {
            iterationNum: number;
            frequency: { cycle: string; day: string; order: number };
        } | null = null;

        if (formData.executionType === EXECUTION_TYPE.MONITORING && formData.schedulingFormGroup) {
            const scheduling = formData.schedulingFormGroup;
            if (scheduling.scheduledDay) {
                const date = new Date(scheduling.scheduledDay);
                const time = scheduling.scheduledTime;
                if (time) {
                    const [hours, minutes] = time.split(':');
                    date.setHours(Number.parseInt(hours, 10), Number.parseInt(minutes, 10), 0, 0);
                } else {
                    // If no time is selected, default to start of day (00:00)
                    date.setHours(0, 0, 0, 0);
                }
                scheduledDate = date;
            }

            if (scheduling.frequency?.cycle) {
                recurrence = 'custom';
                recurringData = {
                    iterationNum: 1, // First iteration
                    frequency: {
                        cycle: scheduling.frequency.cycle,
                        day: scheduling.frequency.day || JOB_RECURRENCE_DAY.MONDAY,
                        order: scheduling.frequency.order || 0,
                    },
                };
            }
        }

        return {
            id: null,
            executionType: formData.executionType,
            entityId: this.jobData().entityId,
            objectType: this.object(),
            name: this.jobData().name,
            baseHillclimbSimId: this.baseHillclimbSimId(),
            hillClimbingPromptAssets: formData.hillClimbingPromptAssets, // Only relevant for hill climbing to send the new prompts to backend
            descriptionSectionData,
            inputsSectionData: {
                currentObjectData,
            },
            dataSourceSectionData,
            dataLogics, // Only used for GGX objects
            scheduledDate, // Scheduling for Monitoring jobs
            recurrence, // Recurrence for Monitoring jobs
            recurringData, // Recurring data for Monitoring jobs
        };
    }

    protected runJob(option: EVENT_TYPE) {
        if (!this.form.valid) {
            FormHelper.showFormErrors(this.form, this.toast);

            return;
        }
        this.runningJob.set(option);
        const formDataAsModel = this.createPayloadForSimulation();
        const payload = JobModel.getForAPI(formDataAsModel, this.object());

        this.simulationService
            .addSimulation(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (option === EVENT_TYPE.REPLACE) {
                    this.simulationService
                        .deleteSimulation(this.jobId())
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe(() => {
                            this.toast.showSuccess(
                                `${this.form.controls.executionType.getRawValue()} added and existing job was replaced successfully`,
                            );
                            this.redirectToJobHistory();
                        });
                } else {
                    this.toast.showSuccess(
                        `${this.form.controls.executionType.getRawValue()} added successfully`,
                    );
                    this.redirectToJobHistory();
                }
            })
            .add(() => this.runningJob.set(null));
    }

    protected onReRunReports() {
        if (!this.form.valid) {
            FormHelper.showFormErrors(this.form, this.toast);

            return;
        }
        if (!(this.service instanceof WithJobService)) throw Error(`Expected Job service`);
        this.runningJob.set(EVENT_TYPE.DEFAULT);

        const formDataAsModel = this.createPayloadForSimulation();
        const payload = JobModel.getForAPI(formDataAsModel, this.object());
        const params: Record<number, JobAdapter['dataLogics'][number]['reportData']> = {};
        payload.dataLogics.forEach(dataLogic => {
            // Only take string and number parameters
            const parameters = Object.entries(dataLogic.reportData || {}).filter(
                ([, val]) => val.type !== REPORT_PARAMETER_TYPE.SINGLE_OBJECT,
            );

            if (parameters.length > 0) {
                params[dataLogic.id] = Object.fromEntries(parameters);
            }
        });

        this.simulationService
            .reRunReports(
                this.jobId(),
                {
                    name: payload.name,
                    description: payload.description,
                    submissionDate: payload.submissionDate,
                },
                params,
            )
            .subscribe(() => {
                this.toast.showSuccess('New Job created to rerun the reports');
                this.redirectToJobHistory();
            })
            .add(() => this.runningJob.set(null));
    }

    protected redirectToJobHistory() {
        const url = RouterHelper.buildRoute({
            object: this.object(), // always required
            id: this.entityId(), // always required
            projectSlug: this.currentProject(),
        });

        this.router.navigateByUrl(`${url}/${TAB_ROUTES.JOBS}`);
    }

    private subscribeToDataLogicChanges(
        dataLogicForm: ArrayItem<JobFormGroup['controls']['dataLogicFormArray']['controls']>,
    ) {
        // Update the suggestions in the code-mirror boxes
        combineLatest({
            tables: dataLogicForm.controls.tableIds.valueChanges.pipe(
                startWith(dataLogicForm.controls.tableIds.value),
            ),
            resources: dataLogicForm.controls.inputFeatureVersionIds.valueChanges.pipe(
                startWith(dataLogicForm.controls.inputFeatureVersionIds.value),
            ),
            annotationQueueId: dataLogicForm.controls.annotationQueueId.valueChanges.pipe(
                startWith(dataLogicForm.controls.annotationQueueId.value),
            ),
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ tables, resources, annotationQueueId }) => {
                const dataLogicIndex =
                    this.form.controls.dataLogicFormArray.controls.indexOf(dataLogicForm);

                this.dataLogicState()[dataLogicIndex].customSuggestions.set([
                    new CodeSuggestionItem({ name: 'duckdb_conn', errorIfUnused: false }),
                    ...tables
                        .map(
                            id =>
                                this.dataLogicInfo.inputTablesList().find(table => table.id === id)
                                    ?.alias,
                        )
                        .filter(alias => !!alias) // Filter out undefined or null values
                        .map(alias => new CodeSuggestionItem({ name: alias })),
                    ...resources
                        .map(
                            x =>
                                this.dataLogicInfo
                                    .inputResourcesList()
                                    .find(res => res.uniqueId === x)?.alias,
                        )
                        .filter(alias => !!alias) // Filter out undefined or null values
                        .map(alias => new CodeSuggestionItem({ name: alias })),
                    ...(annotationQueueId ? [new CodeSuggestionItem({ name: 'queue_data' })] : []),
                ]);
            });

        // Update validators when input type changes
        const dataLogicState =
            this.dataLogicState()[
                this.form.controls.dataLogicFormArray.controls.indexOf(dataLogicForm)
            ];
        combineLatest({
            inputType: dataLogicForm.controls.inputType.valueChanges.pipe(
                startWith(dataLogicForm.controls.inputType.value),
            ),
            selectedDataSource: toObservable(dataLogicState.selectedReportDataSource, {
                injector: this.injector,
            }).pipe(startWith(dataLogicState.selectedReportDataSource())),
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ inputType, selectedDataSource }) => {
                if (inputType === MODEL_INPUT_TYPE.PYTHON_FUNCTION) {
                    dataLogicForm.controls.definition.addValidators(Validators.required);
                    if (selectedDataSource?.id === ANNOTATION_QUEUE_DATASOURCE.id) {
                        dataLogicForm.controls.annotationQueueId.addValidators(Validators.required);
                        dataLogicForm.controls.tableIds.clearValidators();
                    } else {
                        dataLogicForm.controls.tableIds.addValidators(Validators.required);
                        dataLogicForm.controls.annotationQueueId.clearValidators();
                    }
                } else {
                    dataLogicForm.controls.definition.clearValidators();
                    dataLogicForm.controls.tableIds.clearValidators();
                    dataLogicForm.controls.annotationQueueId.clearValidators();
                }

                dataLogicForm.controls.definition.updateValueAndValidity({ emitEvent: false });
                dataLogicForm.controls.tableIds.updateValueAndValidity();
                dataLogicForm.controls.annotationQueueId.updateValueAndValidity();
            });
    }

    /**
     * Adds new datalogic formgroup when user click on Add button in Datalogic section
     */
    protected addDataLogic() {
        const newDataLogicFormGroup = JobModel.getDataLogicFormGroup(
            {
                id: null,
                evaluationType: EVALUATION_TYPE.SINGLE_TURN,
                reportVersionId: null,
                // Parameters and Metrics for the report
                reportParameters: [],
                metricThresholds: [],
                // Data for the report
                featureVersionIds: [],
                tableIds: [],
                definition: null,
                foundationModelVersionIds: [],
                promptVersionIds: [],
                ragVersionIds: [],
                figures: [],
                metrics: [],
            },
            this.dataLogicInfo.inputResourcesList(),
        );
        this.form.controls.dataLogicFormArray.push(newDataLogicFormGroup);
        this.dataLogicState.update(state => [
            // Need to keep dataLogicState in sync with dataLogicFormArray
            ...state,
            {
                // If no dataSource is selected - we use User registered tables as default
                selectedReportDataSource: signal(REGISTERED_TABLES_DATASOURCE),
                reportDetails: signal<ReportAdapter>(null),
                reportParametersListLoading: signal(false),
                metricThresholdsListLoading: signal(false),
                hasOptionalParameters: signal(false),
                showOptionalParameters: signal(false),
                parameterColumns: signal([]),
                parameterFields: signal(null),
                customSuggestions: signal([]),
                showResourceInput: signal(false),
                expectedArguments: signal([]),
            },
        ]);

        this.subscribeToSimulationTypeChanges(newDataLogicFormGroup);
        this.initializeDataSourceTable(
            this.form.controls.dataLogicFormArray.controls.indexOf(newDataLogicFormGroup),
        );
        this.subscribeToDataLogicChanges(newDataLogicFormGroup);
        this.subscribeToDashboardReportsChanges(newDataLogicFormGroup);
    }

    protected dataLogicDelete(dataLogicIndex: number) {
        this.form.controls.dataLogicFormArray.removeAt(dataLogicIndex);
        this.dataLogicState.update(state => state.filter((_, i) => i !== dataLogicIndex));
        this.form.controls.dataLogicFormArray.markAsDirty(); // 'delete' is not part of reactiveForm - so, mark dirty manually
    }

    protected refreshSimulation() {
        this.isJobDataLoaded.set(false);
        this.simulationService
            .getSimulation(this.jobId())
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map(({ result }) => result),
            )
            .subscribe(simulation => {
                this.jobData.set(JobModel.createFromAPI(simulation));
                this.updateJob.emit(this.jobData());
            })
            .add(() => this.isJobDataLoaded.set(true));
    }

    protected onDataGenerationClick(dataLogicIndex: number) {
        this.aiDataGenerationModal.currentDataLogicIndex.set(dataLogicIndex);
        this.aiDataGenerationModal.isOpen.set(true);
        // Reset form
        this.aiDataGenerationModal.form.reset();
        // Populate the descriptions from the current data logic or object
        this.populateModalDescriptions(dataLogicIndex);
    }

    protected closeAiDataGenerationModal() {
        this.aiDataGenerationModal.isOpen.set(false);
        this.aiDataGenerationModal.currentDataLogicIndex.set(null);
        this.aiDataGenerationModal.form.reset();
    }

    protected onReportSelectionClick(event: any, dataLogicIndex: number) {
        event.stopPropagation();
        this.reportDialog.isOpen.set(true);
        this.reportDialog.currentDataLogicIndex.set(dataLogicIndex);
    }

    protected closeReportSelectionModal() {
        this.reportDialog.isOpen.set(false);
        this.reportDialog.currentDataLogicIndex.set(null);
    }

    protected setSelectedReport(reportId: number) {
        if (!this.form) return;
        this.reportDialog.isOpen.set(false);
        // Get report description from the selected report in current data logic
        const dataLogicFormGroup = this.form.controls.dataLogicFormArray.at(
            this.reportDialog.currentDataLogicIndex(),
        );
        dataLogicFormGroup.controls.reportVersionId.patchValue(reportId);
        this.reportDialog.currentDataLogicIndex.set(null);
    }

    protected clearAllFilters() {
        Object.entries(this.reportDialog.filters).forEach(([_, filterItems]: [string, any]) => {
            filterItems.selectedItems.set([]);
        });
        this.reportDialog.searchString.set('');
    }

    protected toggleFilter(filterItem: { isExpanded: WritableSignal<boolean> }) {
        filterItem.isExpanded.update(x => !x);
    }

    protected clearFilter(selectedItems: WritableSignal<string[]>, event?: MouseEvent) {
        selectedItems.set([]);
        event?.stopPropagation();
    }

    protected populateModalDescriptions(dataLogicIndex: number) {
        // Get report description from the selected report in current data logic
        const dataLogicFormGroup = this.form.controls.dataLogicFormArray.at(dataLogicIndex);
        const reportVersionId = dataLogicFormGroup?.get('reportVersionId')?.value;

        let reportDescription = '';
        if (reportVersionId && this.inputDataSources().reportList) {
            const selectedReport = this.inputDataSources().reportList.find(
                (r: any) => r.id === reportVersionId,
            );
            reportDescription = selectedReport?.description || '';
        }

        // Set report description immediately
        this.aiDataGenerationModal.form.patchValue({
            reportDescription,
            objectDescription: '',
            additionalContext: '',
        });

        // Get object description from the API
        this.service
            .getById(this.entityId())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                const objectDescription = response.result?.description || '';
                this.aiDataGenerationModal.form.patchValue({
                    objectDescription,
                });
            });
    }

    protected generateAiData() {
        this.isGeneratingAiData.set(true);

        const formValue = this.aiDataGenerationModal.form.value;
        const dataLogicIndex = this.aiDataGenerationModal.currentDataLogicIndex();

        // Call AI API to generate data
        this.inferenceService
            .generateDataCode(
                formValue.reportDescription || '',
                formValue.objectDescription || '',
                formValue.additionalContext || '',
                this.extractColumns(dataLogicIndex),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ result: generatedData }) => {
                    // Update the definition in the current data logic form
                    if (
                        dataLogicIndex !== null &&
                        this.form.controls.dataLogicFormArray.at(dataLogicIndex)
                    ) {
                        const dataLogicFormGroup =
                            this.form.controls.dataLogicFormArray.at(dataLogicIndex);
                        // Convert the JSON data from API into string[][] format
                        const cellValues = this.convertJsonToCellValues(generatedData);
                        // Update the definition to ensure it's in the right mode
                        // Set table data by storing it in a signal that will be used to update the specific component
                        if (cellValues.length > 0) {
                            dataLogicFormGroup.controls.inputType.setValue(
                                MODEL_INPUT_TYPE.DATASOURCE,
                            );
                            dataLogicFormGroup.controls.dataFileCellValues.setValue(cellValues);
                            dataLogicFormGroup.controls.dataFileCellValues.markAsDirty();
                        }
                    }

                    this.toast.showSuccess('Synthetic data generated successfully');
                    this.closeAiDataGenerationModal();
                },
                error: () =>
                    this.toast.showError('Failed to generate data with AI. Please try again.'),
            })
            .add(() => this.isGeneratingAiData.set(false));
    }

    private extractColumns(dataLogicIndex: number): string {
        const columns: string[] = [];

        // Get expected arguments (mandatory and optional columns)
        this.dataLogicState()
            [dataLogicIndex].expectedArguments()
            .filter(arg => !['history', 'context'].includes(arg.alias)) // Do not include context and history columns
            .forEach(arg => {
                columns.push(`${arg.alias}: (${arg.type})`);
            });

        // Get parameter columns from reports
        const parameterColumns = this.dataLogicState()[dataLogicIndex].parameterColumns();
        parameterColumns.forEach(param => {
            let columnInfo = `${param.alias}: `;
            if (param.description) {
                columnInfo += param.description;
            }
            columnInfo = `${columnInfo.trim()} (String)`;
            columns.push(columnInfo);
        });

        return columns.join(', ');
    }

    private convertJsonToCellValues(jsonData: { columns: string[]; data: string[][] }): string[][] {
        try {
            // Convert to string[][] format expected by DefinitionComponent
            // First row is the header (columns), then the data rows
            const cellValues: string[][] = [
                jsonData.columns.map((col: any) => String(col)),
                ...jsonData.data.map((row: any[]) => row.map((cell: any) => String(cell))),
            ];

            return cellValues;
        } catch (_error) {
            this.toast.showError(
                'Failed to process JSON data from AI API. The data may be malformed. Please try again.',
            );
        }
    }

    protected toggleResourceInput(state: { showResourceInput: WritableSignal<boolean> }) {
        state.showResourceInput.update(x => !x);
    }

    // Scheduling state for Monitoring jobs
    protected minDateValue = new Date();

    protected getTimeValue(): Date | null {
        const timeString = this.form.controls.schedulingFormGroup.controls.scheduledTime.value;
        if (!timeString) return null;

        const [hours, minutes] = timeString.split(':').map(Number);
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);

        return date;
    }

    private subscribeToRecurrencyChanges() {
        // Subscribe to scheduled day changes to update recurrence options
        this.form.controls.schedulingFormGroup.controls.scheduledDay.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(val => {
                this.recurrenceOptions.set(
                    JobModel.getJobSchedulingOptions(
                        val || this.jobData()?.createdDate || new Date().toString(),
                    ),
                );
                // Patch value to the recurrence dropdown
                this.patchFrequencyFormControl();
            });

        // Subscribe to frequency changes
        this.form.controls.schedulingFormGroup.controls.frequency.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(frequency => {
                if (frequency) {
                    this.setRecurrenceDates(frequency.cycle, frequency.day);
                }
            });
    }

    private patchFrequencyFormControl() {
        const freqCtrl = this.form.controls.schedulingFormGroup.controls.frequency;
        const frequency = freqCtrl.value;

        if (frequency?.cycle) {
            const { cycle, day, order } = frequency;

            if (cycle === JOB_RECURRENCE_CYCLE.MONTHLY) {
                const isWeekday = Object.values(JOB_RECURRENCE_DAY).includes(day);
                const isLastWeekday = order === -1;

                let match: { text: string; value: any };

                if (isWeekday) {
                    if (
                        isLastWeekday &&
                        this.recurrenceOptions().some(opt => opt.value.order === -1)
                    ) {
                        // 2. Monthly last weekday
                        match = this.recurrenceOptions().find(
                            opt =>
                                opt.value.cycle === JOB_RECURRENCE_CYCLE.MONTHLY &&
                                opt.value.order === -1,
                        );
                    } else {
                        // 1. Monthly nth weekday
                        match = this.recurrenceOptions().find(
                            opt =>
                                opt.value.cycle === JOB_RECURRENCE_CYCLE.MONTHLY &&
                                opt.value.day !== null,
                        );
                    }
                } else {
                    // 3. Monthly date
                    match = this.recurrenceOptions().find(
                        opt =>
                            opt.value.cycle === JOB_RECURRENCE_CYCLE.MONTHLY &&
                            opt.value.day === null,
                    );
                }

                if (match) {
                    freqCtrl.setValue(match.value);
                }
            } else {
                // Non-monthly
                const match = this.recurrenceOptions().find(opt => opt.value.cycle === cycle);
                if (match) {
                    freqCtrl.setValue(match.value);
                }
            }
        } else {
            // Default fallback
            if (this.recurrenceOptions().length > 0) {
                freqCtrl.setValue(this.recurrenceOptions()[0].value);
            }
        }
    }

    private setRecurrenceDates(cycle: JOB_RECURRENCE_CYCLE, day: JOB_RECURRENCE_DAY) {
        if (cycle) {
            // Use the scheduled day from the form, or fall back to the job's scheduled date, or current date
            const scheduledDate = new Date(
                this.form.controls.schedulingFormGroup.controls.scheduledDay.value || new Date(),
            );

            const mapping: Record<JOB_RECURRENCE_CYCLE, RECURRENCE_CYCLE> = {
                [JOB_RECURRENCE_CYCLE.HOURLY]: RECURRENCE_CYCLE.HOURLY,
                [JOB_RECURRENCE_CYCLE.DAILY]: RECURRENCE_CYCLE.DAILY,
                [JOB_RECURRENCE_CYCLE.WEEKLY]: RECURRENCE_CYCLE.WEEKLY,
                [JOB_RECURRENCE_CYCLE.QUARTERLY]: RECURRENCE_CYCLE.QUARTERLY,
                [JOB_RECURRENCE_CYCLE.ANNUALLY]: RECURRENCE_CYCLE.ANNUALLY,
                [JOB_RECURRENCE_CYCLE.MONTHLY]: day
                    ? RECURRENCE_CYCLE.MONTHLY_ON_SAME_WEEKDAY
                    : RECURRENCE_CYCLE.MONTHLY_ON_SAME_DATE,
            };
            this.recurrenceDates.set(
                CalendarHelper.getRecurrences(scheduledDate, mapping[cycle]).map(e =>
                    new DatePipe('en-US').transform(e, 'EEE, M/dd/yyyy'),
                ),
            );
        } else {
            this.recurrenceDates.set([]);
        }
    }

    protected toggleSignal(sig: WritableSignal<boolean>) {
        sig.update(x => !x);
    }

    private initializeDataSourceTable(index: number) {
        const dataLogicFormGroup = this.form.controls.dataLogicFormArray.at(index);
        dataLogicFormGroup.controls.dataFileCellValues.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dataFileCellValues => {
                const rows = (dataFileCellValues || []).filter(row => row.some(cell => !!cell));
                const columns = rows[0]?.filter(column => !!column) || [];
                const data = rows.slice(1).map(row => row.slice(0, columns.length));
                dataLogicFormGroup.controls.dataFile.setValue(
                    {
                        upload: new File(
                            [new Blob([JSON.stringify({ columns, data })])],
                            'def.json',
                        ),
                    },
                    { emitEvent: false },
                );
            });

        const attachment = dataLogicFormGroup.controls.dataFile.value;
        if (attachment?.id) {
            this.attachmentService
                .readAttachmentAsTableJSON(attachment)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(({ result }) => {
                    dataLogicFormGroup.controls.dataFileCellValues.patchValue(
                        [result.columns, ...result.data],
                        { emitEvent: false },
                    );
                });
        } else {
            this.updateDataLogicInitColumns(index);
        }
    }

    protected updateDataLogicInitColumns(dataLogicIndex: number) {
        const dataLogicFormGroup = this.form.controls.dataLogicFormArray.at(dataLogicIndex);
        // If there is already a dataFile, then we don't need the init columns (its only for empty tables)
        if (dataLogicFormGroup.controls.dataFile.value?.id) return;

        const args = this.dataLogicState()[dataLogicIndex].expectedArguments();
        const params = this.dataLogicState()[dataLogicIndex].parameterColumns();
        const initColumns = [
            ...args.filter(x => x.isMandatory).map(x => x.alias),
            ...params.filter(x => x.isMandatory).map(x => x.alias),
        ];
        dataLogicFormGroup.controls.dataFileCellValues.patchValue([initColumns], {
            emitEvent: false,
        });
    }
}
